/* eslint-disable no-shadow */
import {
    call, takeEvery
} from "redux-saga/effects";

import {
    ADD_REQUESTED,
    ADD_USERS_REQUESTED,
    DELETE_REQUESTED,
    LOAD_ALL_REQUESTED,
    LOAD_REQUESTED,
    REMOVE_USER_REQUESTED,
    UPDATE_REQUESTED,
    UPDATE_USERS_REQUESTED,
    defaultScopeId,
    failAddGroup,
    failAddGroupUsers,
    failDeleteGroup,
    failLoadAllGroups,
    failLoadGroup,
    failRemoveGroupUser,
    failUpdateGroup,
    failUpdateGroupUsers,
    successAddGroup,
    successAddGroupUsers,
    successDeleteGroup,
    successLoadAllGroups,
    successLoadGroup,
    successRemoveGroupUser,
    successUpdateGroup,
    successUpdateGroupUsers
} from "./groups.duck";

import {
    addGroup,
    deleteGroup,
    loadAllGroups,
    loadGroup,
    updateGroup,
    updateGroupUsers,
} from "./groups.api";
import noop from "lodash.noop";
import { ONE_SECOND } from "Globals/constants/constants";

import {
    getId, injectApiWithContextFactory, injectBoundActionCreatorsFactory, takeFirstAndLatest, withRefreshRequest 
} from "Utils/sagaUtils";
import { injectBoundGQLClient } from "Utils/injectBoundGQLClient";

export const withBoundActions = injectBoundActionCreatorsFactory(
    {
        failAddGroup,
        failAddGroupUsers,
        failDeleteGroup,
        failLoadAllGroups,
        failLoadGroup,
        failRemoveGroupUser,
        failUpdateGroup,
        failUpdateGroupUsers,
        successAddGroup,
        successAddGroupUsers,
        successDeleteGroup,
        successLoadAllGroups,
        successLoadGroup,
        successRemoveGroupUser,
        successUpdateGroup,
        successUpdateGroupUsers
    },
    getId,
    defaultScopeId
);

export const withApiContext = injectApiWithContextFactory(
    {
        addGroup,
        deleteGroup,
        loadAllGroups,
        loadGroup,
        updateGroup,
        updateGroupUsers
    },
    injectBoundGQLClient
);

export function* loadSingleGroupSaga({
    data, api: { loadGroup = noop }, actions: { successLoadGroup = noop, failLoadGroup = noop }, resolve = noop, reject = noop 
}){
    try {
        const result = yield loadGroup({ groupId: data });
        yield successLoadGroup(result);
        yield call(resolve);
    } catch (e){
        yield failLoadGroup(e);
        yield call(reject, e);
    }
}

export function* loadGroupsSaga({
    actions: { successLoadAllGroups = noop, failLoadAllGroups = noop },
    api: { loadAllGroups = noop },
    resolve = noop,
    reject = noop,
}) {
    try {
        const { groups: result } = yield loadAllGroups();
        yield successLoadAllGroups(result);
        yield call(resolve);
    } catch (e) {
        yield failLoadAllGroups(e);
        yield call(reject, e);
    }
}

export function* addGroupSaga({
    data, api: { addGroup = noop  }, actions: { successAddGroup = noop, failAddGroup = noop }, resolve = noop, reject = noop 
}){
    try {
        const result = yield addGroup({ formData: data });
        yield successAddGroup(result);
        yield call(resolve);
    } catch (e){
        yield failAddGroup(e);
        yield call(reject, e);
    }
}

export function* updateGroupSaga({
    data, api: { updateGroup = noop }, actions: { successUpdateGroup = noop, failUpdateGroup = noop }, resolve = noop, reject = noop 
}){
    try {
        const result = yield updateGroup({ formData: data });
        yield successUpdateGroup(result);
        yield call(resolve);
    } catch (e){
        yield failUpdateGroup(e);
        yield call(reject, e);
    }
}

export function* removeUserFromGroupSaga({
    data: { groupId, userId },
    api: { updateGroupUsers = noop },
    actions: { successRemoveGroupUser = noop, failRemoveGroupUser = noop },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield updateGroupUsers({
            groupId,
            removeUserIds: [userId],
        });
        yield successRemoveGroupUser(result);
        yield call(resolve);
    } catch (e) {
        yield failRemoveGroupUser(e);
        yield call(reject, e);
    }
}

export function* addUsersToGroupSaga({
    data: { groupId, users = []},
    api: { updateGroupUsers = noop },
    actions: { successAddGroupUsers = noop, failAddGroupUsers = noop },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield updateGroupUsers({
            groupId,
            addUserIds: users.map(({ value }) => value),
        });
        yield successAddGroupUsers(result);
        yield call(resolve);
    } catch (e) {
        yield failAddGroupUsers(e);
        yield call(reject, e);
    }
}

export function* updateUserGroupsSaga({
    data: { groupId, initialUsers, updateUsers },
    api: { updateGroupUsers = noop },
    actions: { successUpdateGroupUsers = noop, failUpdateGroupUsers = noop },
    resolve = noop,
    reject = noop,
}) {
    const initialUserIds = initialUsers.map(({ value }) => value);
    const updateUserIds = updateUsers.map(({ value }) => value);
    const removeUserIds = initialUserIds.filter(
        id => !updateUserIds.includes(id)
    );
    const addUserIds = updateUserIds.filter(
        id => !initialUserIds.includes(id)
    );
    try {
        const result = yield updateGroupUsers({
            groupId,
            removeUserIds,
            addUserIds,
        });
        yield successUpdateGroupUsers(result);
        yield call(resolve);
    } catch (e) {
        yield failUpdateGroupUsers(e);
        yield call(reject, e);
    }
}

export function* deleteGroupSaga({
    data: { id }, api: { deleteGroup = noop }, actions: { successDeleteGroup = noop, failDeleteGroup = noop }, resolve = noop, reject = noop 
}){
    try {
        const result = yield deleteGroup({ id });
        yield successDeleteGroup(result);
        yield call(resolve);
    } catch (e){
        yield failDeleteGroup(e);
        yield call(reject, e);
    }
}

export default () => function* rootSaga() {
    yield takeFirstAndLatest(
        LOAD_ALL_REQUESTED,
        withRefreshRequest(withApiContext(withBoundActions(loadGroupsSaga))),
        ONE_SECOND,
    );
    yield takeEvery(LOAD_REQUESTED, withRefreshRequest(withApiContext(withBoundActions(loadSingleGroupSaga))));
    yield takeEvery(ADD_REQUESTED, withApiContext(withBoundActions(addGroupSaga)));
    yield takeEvery(UPDATE_REQUESTED, withApiContext(withBoundActions(updateGroupSaga)));
    yield takeEvery(UPDATE_USERS_REQUESTED, withApiContext(withBoundActions(updateUserGroupsSaga)));
    yield takeEvery(DELETE_REQUESTED, withApiContext(withBoundActions(deleteGroupSaga)));
    yield takeEvery(REMOVE_USER_REQUESTED, withApiContext(withBoundActions(removeUserFromGroupSaga)));
    yield takeEvery(ADD_USERS_REQUESTED, withApiContext(withBoundActions(addUsersToGroupSaga)));
};
